import { onBeforeUnmount, onMounted, ref, watch } from "vue";
import { usePointsOfInterest } from "../usePointsOfInterest";
import { useI18n } from "vue3-i18n";
import { escapeHtml, isValidUrl, isValidCoordinates } from "../../utils/security";

export function usePointsOfInterestLayer(map, language, handleFullScreenModalOpen) {
  let markers = [];
  const { t } = useI18n();
  const { data: pointsOfInterest, isSuccess } = usePointsOfInterest();
  let AdvancedMarkerElement = null;

  // Function to add markers to the map
  const addMarkers = () => {
    if (!map.value || !pointsOfInterest.value || pointsOfInterest.value.length === 0 || !AdvancedMarkerElement) {
      return;
    }

    pointsOfInterest.value.forEach((poi) => {
      // Validate coordinates before creating marker
      if (!isValidCoordinates(poi.coords?.lat, poi.coords?.lng)) {
        console.warn(`Invalid coordinates for POI ${poi.id}, skipping marker creation.`);
        return;
      }

      // Validate image URL
      const imageUrl = poi.imgs && poi.imgs[0] ? poi.imgs[0] : '';
      if (!isValidUrl(imageUrl)) {
        console.warn(`Invalid image URL for POI ${poi.id}, using placeholder.`);
      }

      try {
        // Create custom icon element for AdvancedMarkerElement
        const iconElement = document.createElement('div');
        iconElement.style.width = '40px';
        iconElement.style.height = '40px';
        const baseUrl = import.meta.env.BASE_URL || '/';
        iconElement.innerHTML = `<img src="${baseUrl}images/location-sign.png" style="width: 100%; height: 100%; display: block;" alt="location marker" />`;

        // Create AdvancedMarkerElement instead of deprecated Marker
        const marker = new AdvancedMarkerElement({
          position: poi.coords,
          map: map.value,
          title: poi.id,
          content: iconElement,
        });

        // Get translations from content.json based on current language, fallback to i18n if not available
        const poiTranslations = poi.translations?.[language.value] || {};
        const poiName = escapeHtml(poiTranslations.name || t(`points.${poi.id}.name`) || poi.id);
        const poiDescription = escapeHtml(poiTranslations.description || t(`points.${poi.id}.description`) || '');
        const seeMoreText = escapeHtml(t('seeMorePhotos') || 'See more photos');
        const safeImageUrl = isValidUrl(imageUrl) ? escapeHtml(imageUrl) : '';

        // Create info window with sanitized content
        const infoWindow = new google.maps.InfoWindow({
          content: `
          <div style="text-align: center; max-width: 300px; display: flex; align-items:center; justify-content: center; flex-direction: column">
            ${safeImageUrl ? `<img style="width: 70%; height: auto; border-radius: 8px;" src="${safeImageUrl}" alt="${poiName}" />` : ''}
            <h4 style="margin: 10px 0; font-size: 1.2em; color: #333;">${poiName}</h4>
            <p style="margin: 0; font-size: 0.9em; color: #666;">${poiDescription}</p>
            <button id="poi-btn-${poi.id}" style="margin-top: 10px; padding: 5px 10px; background-color: #007bff; color: white; border: none; border-radius: 20px; cursor: pointer;">${seeMoreText}</button>
          </div>
          `,
        });

        // Add event listener for info window content
        google.maps.event.addListener(infoWindow, 'domready', () => {
          const button = document.getElementById(`poi-btn-${poi.id}`);
          if (button) {
            button.addEventListener("click", () => {
              handleFullScreenModalOpen(poi.id);
            });
          }
        });

        // AdvancedMarkerElement uses addEventListener instead of addListener
        marker.addEventListener("click", () => {
          markers.forEach((m) => {
            if (m.infoWindow) {
              m.infoWindow.close();
            }
          });
          infoWindow.open({
            anchor: marker,
            map: map.value,
            shouldFocus: true,
          });
        });

        marker.infoWindow = infoWindow;
        markers.push(marker);
      } catch (error) {
        console.error(`Error creating marker for POI ${poi.id}:`, error);
      }
    });
  };

  // Function to remove markers from the map
  const removeMarkers = () => {
    markers?.forEach((marker) => {
      marker.map = null; // AdvancedMarkerElement uses .map property instead of setMap()
    });
    markers = [];
  };

  // Load the marker library when component mounts
  onMounted(async () => {
    try {
      const markerLib = await google.maps.importLibrary("marker");
      AdvancedMarkerElement = markerLib.AdvancedMarkerElement;

      if (!AdvancedMarkerElement) {
        console.error('Advanced marker element library failed to load properly.');
        return;
      }

      // Once library is loaded, add markers if data is ready
      if (isSuccess.value && pointsOfInterest.value?.length > 0 && map.value) {
        addMarkers();
      }
    } catch (err) {
      console.error('Error importing Google Maps marker library:', err);
    }
  });

  // Add markers when dependencies change (but only after library is loaded)
  watch([map, pointsOfInterest, isSuccess, language], () => {
    removeMarkers();
    if (isSuccess.value && pointsOfInterest.value?.length > 0 && map.value && AdvancedMarkerElement) {
      addMarkers();
    }
  });

  // Remove markers before component is unmounted
  onBeforeUnmount(() => {
    removeMarkers();
  });

  return map;
}
